/**
 * Iter 31.297 — Radio Pool Consolidation panel.
 *
 * Six's brief: ~700 tracks are scattered across `_Filed/radio_stations/`
 * subfolders plus some orphaned "0 folders" he moved out. Before
 * rebuilding uniform radio stations, everything needs to be in one
 * place for offline triage. This tile fires a Drive-side move that
 * gathers every `.mp3` under `_Filed/` into `_Filed/radio_raw_pool/`,
 * deduped by (filename lowercased, size).
 *
 * UX: dry-run first (shows what WOULD move), then "Commit move" fires
 * the real thing. Head-count of the pool folder is always visible.
 * Once the pool is populated, Six uses Drive's native "download folder"
 * feature to grab the whole thing as a ZIP for offline reorg.
 */
import React, { useCallback, useEffect, useReducer } from "react";
import { api } from "@/lib/api";
import {
    Radio, Loader2, AlertTriangle, PlayCircle,
    FolderInput, CheckCircle2, ExternalLink,
} from "lucide-react";

const INITIAL = {
    status: null,        // {available, count, total_mb, pool_link, sample}
    statusLoading: true,
    statusErr: null,
    // manifest = dry-run result
    manifest: null,      // {scanned, winners, duplicates, sample, pool_link}
    manifestLoading: false,
    manifestErr: null,
    // commit
    commitLoading: false,
    commitErr: null,
    commitResult: null,  // {moved, trashed, ...}
    trashDuplicates: true,
};

function reducer(s, a) {
    switch (a.type) {
        case "status_ok": return { ...s, status: a.status, statusLoading: false, statusErr: null };
        case "status_err": return { ...s, statusLoading: false, statusErr: a.err };
        case "manifest_busy": return { ...s, manifestLoading: true, manifestErr: null, commitResult: null };
        case "manifest_ok": return { ...s, manifest: a.manifest, manifestLoading: false, manifestErr: null };
        case "manifest_err": return { ...s, manifestLoading: false, manifestErr: a.err };
        case "commit_busy": return { ...s, commitLoading: true, commitErr: null };
        case "commit_ok": return { ...s, commitLoading: false, commitResult: a.result, commitErr: null };
        case "commit_err": return { ...s, commitLoading: false, commitErr: a.err };
        case "toggle_trash": return { ...s, trashDuplicates: !s.trashDuplicates };
        default: return s;
    }
}

export default function RadioPoolPanel() {
    const [s, dispatch] = useReducer(reducer, INITIAL);

    const loadStatus = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/radio/pool-status");
            dispatch({ type: "status_ok", status: data });
        } catch (e) {
            dispatch({
                type: "status_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, []);

    useEffect(() => { loadStatus(); }, [loadStatus]);

    const dryRun = useCallback(async () => {
        dispatch({ type: "manifest_busy" });
        try {
            const { data } = await api.post("/admin/radio/consolidate-pool", {
                dry_run: true, trash_duplicates: s.trashDuplicates,
            });
            dispatch({ type: "manifest_ok", manifest: data });
        } catch (e) {
            dispatch({
                type: "manifest_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, [s.trashDuplicates]);

    const commit = useCallback(async () => {
        if (!s.manifest) return;
        const winners = s.manifest.winners;
        const dups = s.manifest.duplicates;
        const msg = (
            `MOVE ${winners} MP3(s) into _Filed/radio_raw_pool/\n` +
            (s.trashDuplicates ? `TRASH ${dups} duplicate(s)\n\n` : `LEAVE ${dups} duplicate(s) in place\n\n`) +
            "This modifies Drive. Continue?"
        );
        if (!window.confirm(msg)) return;
        dispatch({ type: "commit_busy" });
        try {
            const { data } = await api.post("/admin/radio/consolidate-pool", {
                dry_run: false, trash_duplicates: s.trashDuplicates,
            });
            dispatch({ type: "commit_ok", result: data });
            loadStatus();
        } catch (e) {
            dispatch({
                type: "commit_err",
                err: `[HTTP ${e.response?.status || "?"}] ${e.response?.data?.detail || e.message}`,
            });
        }
    }, [s.manifest, s.trashDuplicates, loadStatus]);

    const stat = s.status;

    return (
        <div
            className="paper-card border-2 border-ink/30 p-5 mb-6"
            data-testid="radio-pool-panel"
        >
            <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading uppercase tracking-[0.22em] text-sm text-ink flex items-center gap-2">
                    <Radio className="w-4 h-4 text-stamp-red" />
                    Radio Pool Consolidation
                </h2>
                <button
                    type="button"
                    onClick={loadStatus}
                    disabled={s.statusLoading}
                    data-testid="rp-refresh"
                    className="font-heading uppercase tracking-[0.16em] text-[10px] text-ink-soft hover:text-stamp-red disabled:opacity-50"
                >
                    {s.statusLoading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "refresh"}
                </button>
            </div>

            <div className="font-body text-[11px] text-ink-soft mb-3 leading-relaxed">
                Iter 31.297 — one-shot organiser. Gathers every <code className="font-mono">.mp3</code> under{" "}
                <code className="font-mono">_Filed/</code> into{" "}
                <code className="font-mono">_Filed/radio_raw_pool/</code> and dedupes by (name, size).
                Use Drive&apos;s native &quot;Download folder&quot; to grab the pool as a ZIP for offline triage.
            </div>

            {s.statusErr ? (
                <div className="mb-3 label-typed text-[11px] leading-relaxed border-l-2 border-stamp-red pl-3 text-stamp-red flex items-start gap-1" data-testid="rp-status-err">
                    <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{s.statusErr}</span>
                </div>
            ) : null}

            {/* Current pool head-count */}
            {stat ? (
                stat.available ? (
                    <div
                        className="border border-ink/20 p-3 mb-3 flex items-center justify-between gap-3"
                        data-testid="rp-pool-status"
                    >
                        <div className="font-body text-[11px] text-ink-soft">
                            Pool currently holds{" "}
                            <span className="font-mono text-stamp-red font-bold">{stat.count}</span>{" "}
                            track{stat.count === 1 ? "" : "s"}
                            {stat.total_mb ? (
                                <span className="text-ink-faint">
                                    {" "}· {stat.total_mb} MB
                                </span>
                            ) : null}
                        </div>
                        {stat.pool_link ? (
                            <a
                                href={stat.pool_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="rp-pool-link"
                                className="font-heading uppercase text-[10px] tracking-[0.22em] text-ink-soft hover:text-stamp-red inline-flex items-center gap-1"
                            >
                                Open in Drive <ExternalLink className="w-3 h-3" />
                            </a>
                        ) : null}
                    </div>
                ) : (
                    <div
                        className="border border-ink/20 p-3 mb-3 font-body text-[11px] text-ink-faint italic"
                        data-testid="rp-unavailable"
                    >
                        Pool unavailable — {stat.reason || "unknown reason"}
                    </div>
                )
            ) : null}

            {/* Dry-run manifest */}
            {s.manifest ? (
                <div
                    className="border border-stamp-red/40 bg-stamp-red/5 p-3 mb-3"
                    data-testid="rp-manifest"
                >
                    <div className="label-typed text-[10px] text-stamp-red mb-2">
                        Dry-run manifest
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-2 text-center">
                        <div>
                            <div className="font-mono text-xl text-ink font-bold" data-testid="rp-manifest-winners">{s.manifest.winners}</div>
                            <div className="font-body text-[10px] text-ink-soft uppercase tracking-wider">to move</div>
                        </div>
                        <div>
                            <div className="font-mono text-xl text-ink-soft" data-testid="rp-manifest-dups">{s.manifest.duplicates}</div>
                            <div className="font-body text-[10px] text-ink-soft uppercase tracking-wider">duplicates</div>
                        </div>
                        <div>
                            <div className="font-mono text-xl text-ink-faint" data-testid="rp-manifest-already">{s.manifest.already_in_pool}</div>
                            <div className="font-body text-[10px] text-ink-soft uppercase tracking-wider">already pooled</div>
                        </div>
                    </div>
                    {s.manifest.sample?.length ? (
                        <details className="mt-2">
                            <summary className="font-body text-[10px] text-ink-soft cursor-pointer hover:text-stamp-red">
                                First {s.manifest.sample.length} winners…
                            </summary>
                            <ul className="mt-1 max-h-40 overflow-y-auto font-mono text-[10px] text-ink-soft space-y-0.5">
                                {s.manifest.sample.map((f, i) => (
                                    <li key={i} className="truncate">
                                        <span className="text-ink">{f.name}</span>
                                        {f.source_path ? (
                                            <span className="text-ink-faint"> ← {f.source_path}</span>
                                        ) : null}
                                    </li>
                                ))}
                            </ul>
                        </details>
                    ) : null}
                </div>
            ) : null}

            {/* Commit result */}
            {s.commitResult ? (
                <div
                    className="border-2 border-emerald-700 bg-emerald-50 p-3 mb-3"
                    data-testid="rp-commit-result"
                >
                    <div className="label-typed text-[10px] text-emerald-800 flex items-center gap-1 mb-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Move committed
                    </div>
                    <div className="font-body text-[11px] text-ink-soft">
                        Moved <span className="font-mono text-stamp-red font-bold">{s.commitResult.moved}</span> track(s) into the pool.
                        {s.commitResult.trashed > 0 ? (
                            <>
                                {" "}Trashed{" "}
                                <span className="font-mono text-ink font-bold">{s.commitResult.trashed}</span>{" "}
                                duplicate(s).
                            </>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {/* Errors */}
            {s.manifestErr ? (
                <div className="text-[11px] text-stamp-red font-body mb-2" data-testid="rp-manifest-err">
                    {s.manifestErr}
                </div>
            ) : null}
            {s.commitErr ? (
                <div className="text-[11px] text-stamp-red font-body mb-2" data-testid="rp-commit-err">
                    {s.commitErr}
                </div>
            ) : null}

            {/* Options */}
            <label
                className="flex items-center gap-2 mb-3 cursor-pointer"
                data-testid="rp-trash-toggle"
            >
                <input
                    type="checkbox"
                    checked={s.trashDuplicates}
                    onChange={() => dispatch({ type: "toggle_trash" })}
                    className="w-3 h-3 accent-stamp-red"
                />
                <span className="font-body text-[11px] text-ink-soft">
                    Trash duplicates (same name + size) after moving winners
                </span>
            </label>

            {/* Action row */}
            <div className="flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={dryRun}
                    disabled={s.manifestLoading || s.commitLoading}
                    data-testid="rp-dry-run"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-ink/40 text-ink hover:border-stamp-red hover:text-stamp-red disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                    {s.manifestLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
                    Dry-run
                </button>
                <button
                    type="button"
                    onClick={commit}
                    disabled={!s.manifest || s.commitLoading || s.manifestLoading}
                    data-testid="rp-commit"
                    className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red/10 disabled:opacity-40 disabled:hover:bg-transparent inline-flex items-center gap-1.5"
                >
                    {s.commitLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FolderInput className="w-3 h-3" />}
                    Commit move
                </button>
                {stat?.available && stat?.pool_link ? (
                    <a
                        href={stat.pool_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="rp-open-pool"
                        className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border border-ink/30 text-ink-soft hover:border-stamp-red hover:text-stamp-red inline-flex items-center gap-1.5 ml-auto"
                    >
                        Open pool folder <ExternalLink className="w-3 h-3" />
                    </a>
                ) : null}
            </div>
        </div>
    );
}
